/**
 * Every number, date, range, duration and currency the interface prints.
 *
 * All of it goes through `Intl`, bound to the *active locale* — which is the defect this
 * phase exists to fix: the first build formatted `it-IT` under English copy, so an English
 * interface read *2,2 Mln* and *633,80 €*. Formatting now follows the language, so the two
 * can never disagree again.
 *
 * Two ways in, same functions underneath:
 *
 *   const f = useFormat()           // inside a component — bound to the active locale
 *   f.compact(2_200_000)            // '2.2M'  /  '2,2 Mln'
 *   f.range('2026-07-11', '2026-08-07')
 *
 *   formatDate(locale, value)       // outside React — the locale is the first argument
 *
 * Notes that save a bug report:
 *   - A plain `YYYY-MM-DD` string is a *calendar date*. It is formatted in UTC whatever the
 *     timezone argument says, so a day never slips backwards in a negative offset.
 *   - `timeZone` is the brand's zone (`useAuth().activeTimezone`), not the browser's. Pass it
 *     for anything that has an hour attached; omit it for a calendar date.
 *   - Weekday and month *names* come from `weekdayNames` / `monthNames`, never from the
 *     catalogue: `Intl` already knows that Italian writes them lowercase.
 *   - The week starts on Monday in both locales — this is a scheduling tool.
 */

import { useMemo } from 'react'

import type { Locale } from './shared'
import { DEFAULT_CURRENCY, intlLocaleOf } from './shared'
import { useActiveLocale } from './useLocaleStore'

export type DateInput = Date | string | number

export type NameWidth = 'long' | 'short' | 'narrow'

export interface ZoneOption {
  /** IANA zone, e.g. `Europe/Rome`. Ignored for plain `YYYY-MM-DD` dates. */
  timeZone?: string
}

/* ── Intl caches ──────────────────────────────────────────────────────────────
   Constructing an Intl formatter costs more than using one, and these are used in table
   rows and chart ticks. Options objects are small and closed, so a string key is enough. */

const numberFormats = new Map<string, Intl.NumberFormat>()
const dateFormats = new Map<string, Intl.DateTimeFormat>()
const relativeFormats = new Map<string, Intl.RelativeTimeFormat>()
const listFormats = new Map<string, Intl.ListFormat>()

const keyFor = (intlLocale: string, options: object): string =>
  `${intlLocale}|${JSON.stringify(options)}`

const numberFormat = (locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const intlLocale = intlLocaleOf(locale)
  const key = keyFor(intlLocale, options)
  const cached = numberFormats.get(key)
  if (cached) return cached
  const formatter = new Intl.NumberFormat(intlLocale, options)
  numberFormats.set(key, formatter)
  return formatter
}

const dateFormat = (locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const intlLocale = intlLocaleOf(locale)
  const key = keyFor(intlLocale, options)
  const cached = dateFormats.get(key)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat(intlLocale, options)
  dateFormats.set(key, formatter)
  return formatter
}

const relativeFormat = (
  locale: Locale,
  options: Intl.RelativeTimeFormatOptions,
): Intl.RelativeTimeFormat => {
  const intlLocale = intlLocaleOf(locale)
  const key = keyFor(intlLocale, options)
  const cached = relativeFormats.get(key)
  if (cached) return cached
  const formatter = new Intl.RelativeTimeFormat(intlLocale, options)
  relativeFormats.set(key, formatter)
  return formatter
}

const listFormat = (locale: Locale, options: Intl.ListFormatOptions): Intl.ListFormat => {
  const intlLocale = intlLocaleOf(locale)
  const key = keyFor(intlLocale, options)
  const cached = listFormats.get(key)
  if (cached) return cached
  const formatter = new Intl.ListFormat(intlLocale, options)
  listFormats.set(key, formatter)
  return formatter
}

/* ── date input ───────────────────────────────────────────────────────────── */

const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/

/** `2026-08-07` — a calendar date, with no hour and therefore no zone. */
export const isPlainDate = (value: DateInput): value is string =>
  typeof value === 'string' && PLAIN_DATE.test(value)

export const toDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value))

/** A calendar date is read in UTC so it prints the day it names, in every zone. */
const zoneOf = (value: DateInput, timeZone: string | undefined): string | undefined =>
  isPlainDate(value) ? 'UTC' : timeZone

const withZone = (
  options: Intl.DateTimeFormatOptions,
  value: DateInput,
  timeZone: string | undefined,
): Intl.DateTimeFormatOptions => {
  const zone = zoneOf(value, timeZone)
  return zone === undefined ? options : { ...options, timeZone: zone }
}

/* ── numbers ──────────────────────────────────────────────────────────────── */

/**
 * Italian groups from five digits by default (CLDR `minimumGroupingDigits: 2`), so `1234`
 * would print bare next to a grouped `12.345` in the same column. docs/I18N.md asks for
 * `1.234,56`, and a table of figures reads better grouped, so grouping is always on.
 * `true` is the spec's alias for `'always'`, and it types under the ES2022 lib.
 */
const GROUPED: Intl.NumberFormatOptions = { useGrouping: true }

export const formatNumber = (
  locale: Locale,
  value: number,
  options: Intl.NumberFormatOptions = {},
): string => numberFormat(locale, { ...GROUPED, ...options }).format(value)

/** `1.234` / `1,234`. No decimals, ever. */
export const formatInteger = (locale: Locale, value: number): string =>
  numberFormat(locale, { ...GROUPED, maximumFractionDigits: 0 }).format(value)

/** Fixed decimals: `1.234,50` in Italian, `1,234.50` in English. */
export const formatDecimal = (locale: Locale, value: number, digits = 1): string =>
  numberFormat(locale, {
    ...GROUPED,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)

/** `12.4K` · `2.2M` / `12.400` · `2,2 Mln` — axis ticks and dense tiles. */
export const formatCompactNumber = (locale: Locale, value: number): string =>
  numberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: Math.abs(value) < 10_000 ? 0 : 1,
  }).format(value)

/** Takes a percentage, not a fraction: `formatPercent(locale, 12.4)` → `12.4%` / `12,4%`. */
export const formatPercent = (locale: Locale, value: number, digits = 1): string =>
  numberFormat(locale, { style: 'percent', maximumFractionDigits: digits }).format(value / 100)

/** `+12` / `−3`. Zero stays unsigned. */
export const formatSigned = (locale: Locale, value: number, digits = 0): string =>
  numberFormat(locale, {
    ...GROUPED,
    signDisplay: 'exceptZero',
    maximumFractionDigits: digits,
  }).format(value)

/** `€18.00` in English, `18,00 €` in Italian — the symbol's side is the locale's business. */
export const formatCurrency = (
  locale: Locale,
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  options: Intl.NumberFormatOptions = {},
): string =>
  numberFormat(locale, { ...GROUPED, style: 'currency', currency, ...options }).format(amount)

/** Stripe and the plan tables speak in cents; nothing else should have to divide. */
export const formatCurrencyCents = (
  locale: Locale,
  cents: number,
  currency: string = DEFAULT_CURRENCY,
  options: Intl.NumberFormatOptions = {},
): string => formatCurrency(locale, cents / 100, currency, options)

export const formatBytes = (locale: Locale, bytes: number): string => {
  if (bytes < 1024) return `${formatInteger(locale, bytes)} B`
  const units = ['kilobyte', 'megabyte', 'gigabyte'] as const
  let value = bytes / 1024
  let index = 0
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024
    index += 1
  }
  return numberFormat(locale, {
    style: 'unit',
    unit: units[index] ?? 'gigabyte',
    unitDisplay: 'short',
    maximumFractionDigits: value < 10 ? 1 : 0,
  }).format(value)
}

/** `Facebook, Instagram and TikTok` / `Facebook, Instagram e TikTok`. */
export const formatList = (
  locale: Locale,
  items: readonly string[],
  type: Intl.ListFormatType = 'conjunction',
): string => listFormat(locale, { style: 'long', type }).format([...items])

/* ── dates ────────────────────────────────────────────────────────────────── */

/** `7 Aug 2026` / `7 ago 2026`. */
export const formatDate = (locale: Locale, value: DateInput, options: ZoneOption = {}): string =>
  dateFormat(
    locale,
    withZone({ day: 'numeric', month: 'short', year: 'numeric' }, value, options.timeZone),
  ).format(toDate(value))

/** `7 August 2026` / `7 agosto 2026` — for a report cover, not a table row. */
export const formatDateLong = (locale: Locale, value: DateInput, options: ZoneOption = {}): string =>
  dateFormat(
    locale,
    withZone({ day: 'numeric', month: 'long', year: 'numeric' }, value, options.timeZone),
  ).format(toDate(value))

/** `7 Aug` / `7 ago` — the year is implied by the range around it. */
export const formatDateShort = (locale: Locale, value: DateInput, options: ZoneOption = {}): string =>
  dateFormat(locale, withZone({ day: 'numeric', month: 'short' }, value, options.timeZone)).format(
    toDate(value),
  )

/** `07/08/2026` — dense columns and inputs. */
export const formatDateNumeric = (
  locale: Locale,
  value: DateInput,
  options: ZoneOption = {},
): string =>
  dateFormat(
    locale,
    withZone({ day: '2-digit', month: '2-digit', year: 'numeric' }, value, options.timeZone),
  ).format(toDate(value))

/** `14:30`. */
export const formatTime = (locale: Locale, value: DateInput, options: ZoneOption = {}): string =>
  dateFormat(
    locale,
    withZone({ hour: '2-digit', minute: '2-digit' }, value, options.timeZone),
  ).format(toDate(value))

/** `7 Aug 2026, 14:30` / `7 ago 2026, 14:30`. */
export const formatDateTime = (locale: Locale, value: DateInput, options: ZoneOption = {}): string =>
  dateFormat(
    locale,
    withZone(
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
      value,
      options.timeZone,
    ),
  ).format(toDate(value))

/** `Fri` / `ven` — lowercase in Italian, because `Intl` knows and we do not argue. */
export const formatWeekday = (
  locale: Locale,
  value: DateInput,
  width: NameWidth = 'short',
  options: ZoneOption = {},
): string =>
  dateFormat(locale, withZone({ weekday: width }, value, options.timeZone)).format(toDate(value))

/** `August 2026` / `agosto 2026` — the month grid's caption. */
export const formatMonthYear = (
  locale: Locale,
  value: DateInput,
  options: ZoneOption = {},
): string =>
  dateFormat(locale, withZone({ month: 'long', year: 'numeric' }, value, options.timeZone)).format(
    toDate(value),
  )

interface CalendarParts {
  year: string
  month: string
  day: string
}

const calendarParts = (
  locale: Locale,
  value: DateInput,
  timeZone: string | undefined,
): CalendarParts => {
  const parts = dateFormat(
    locale,
    withZone({ year: 'numeric', month: '2-digit', day: '2-digit' }, value, timeZone),
  ).formatToParts(toDate(value))
  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? ''
  return { year: read('year'), month: read('month'), day: read('day') }
}

/**
 * `1 – 30 Sep 2026` · `11 Jul – 7 Aug 2026` · `31 Dec 2025 – 2 Jan 2026`.
 * Whatever the two ends share is written once, at the end where the reader expects it.
 */
export const formatDateRange = (
  locale: Locale,
  from: DateInput,
  to: DateInput,
  options: ZoneOption = {},
): string => {
  const start = calendarParts(locale, from, options.timeZone)
  const end = calendarParts(locale, to, options.timeZone)

  if (start.year === end.year && start.month === end.month && start.day === end.day) {
    return formatDate(locale, to, options)
  }
  if (start.year === end.year && start.month === end.month) {
    const startDay = dateFormat(
      locale,
      withZone({ day: 'numeric' }, from, options.timeZone),
    ).format(toDate(from))
    return `${startDay} – ${formatDate(locale, to, options)}`
  }
  if (start.year === end.year) {
    return `${formatDateShort(locale, from, options)} – ${formatDate(locale, to, options)}`
  }
  return `${formatDate(locale, from, options)} – ${formatDate(locale, to, options)}`
}

/* ── relative time ────────────────────────────────────────────────────────── */

const RELATIVE_UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
  ['second', 1],
]

/** `4 minutes ago` · `yesterday` · `in 2 days` / `4 minuti fa` · `ieri` · `tra 2 giorni`. */
export const formatRelativeTime = (
  locale: Locale,
  value: DateInput,
  base: DateInput = new Date(),
): string => {
  const seconds = (toDate(value).getTime() - toDate(base).getTime()) / 1000
  const formatter = relativeFormat(locale, { numeric: 'auto' })
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size || unit === 'second') {
      const amount = Math.round(seconds / size)
      // Never say "0 seconds ago" — round the last second away from zero.
      const safe = amount === 0 && unit === 'second' ? (seconds < 0 ? -1 : 1) : amount
      return formatter.format(safe, unit)
    }
  }
  return formatter.format(0, 'second')
}

const UNIT_FOR_COMPACT: Readonly<Record<string, Intl.NumberFormatOptions['unit']>> = {
  second: 'second',
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'week',
}

/**
 * `4m` · `3h` · `2d` / `4 min` · `3 h` · `2 gg` — for dense rows where "ago" is implied by
 * the column. Anything older than four weeks prints as a date instead.
 */
export const formatRelativeCompact = (
  locale: Locale,
  value: DateInput,
  base: DateInput = new Date(),
): string => {
  const then = toDate(value)
  const seconds = Math.abs((then.getTime() - toDate(base).getTime()) / 1000)
  if (seconds >= 2_419_200) return formatDateShort(locale, then)

  for (const [unit, size] of RELATIVE_UNITS) {
    const numberUnit = UNIT_FOR_COMPACT[unit]
    if (numberUnit === undefined) continue
    if (seconds >= size || unit === 'second') {
      return numberFormat(locale, {
        style: 'unit',
        unit: numberUnit,
        unitDisplay: 'narrow',
        maximumFractionDigits: 0,
      }).format(Math.max(1, Math.round(seconds / size)))
    }
  }
  return formatDateShort(locale, then)
}

/* ── durations ────────────────────────────────────────────────────────────── */

export type DurationStyle = 'narrow' | 'short' | 'long'

const durationPart = (
  locale: Locale,
  value: number,
  unit: 'hour' | 'minute' | 'second',
  style: DurationStyle,
): string =>
  numberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: style,
    maximumFractionDigits: 0,
  }).format(value)

/** Minutes → `3h 12m` / `3 h 12 min`. */
export const formatDuration = (
  locale: Locale,
  minutes: number,
  style: DurationStyle = 'narrow',
): string => {
  const total = Math.max(0, Math.round(minutes))
  const hours = Math.floor(total / 60)
  const rest = total % 60
  if (hours === 0) return durationPart(locale, rest, 'minute', style)
  if (rest === 0) return durationPart(locale, hours, 'hour', style)
  return `${durationPart(locale, hours, 'hour', style)} ${durationPart(locale, rest, 'minute', style)}`
}

/** Seconds → `45s`, then hands over to the minute form. */
export const formatDurationSeconds = (
  locale: Locale,
  seconds: number,
  style: DurationStyle = 'narrow',
): string => {
  const total = Math.max(0, Math.round(seconds))
  if (total < 60) return durationPart(locale, total, 'second', style)
  return formatDuration(locale, total / 60, style)
}

/* ── name lists ───────────────────────────────────────────────────────────── */

/** 2024-01-01 was a Monday: a stable reference week, formatted in UTC. */
const REFERENCE_MONDAY_UTC = Date.UTC(2024, 0, 1)

/** Seven names, Monday first — the week this product starts on, in both locales. */
export const weekdayNames = (locale: Locale, width: NameWidth = 'short'): readonly string[] => {
  const formatter = dateFormat(locale, { weekday: width, timeZone: 'UTC' })
  return Array.from({ length: 7 }, (_unused, index) =>
    formatter.format(new Date(REFERENCE_MONDAY_UTC + index * 86_400_000)),
  )
}

/** Twelve names, January first. */
export const monthNames = (locale: Locale, width: NameWidth = 'long'): readonly string[] => {
  const formatter = dateFormat(locale, { month: width, timeZone: 'UTC' })
  return Array.from({ length: 12 }, (_unused, index) =>
    formatter.format(new Date(Date.UTC(2024, index, 15))),
  )
}

/* ── the hook ─────────────────────────────────────────────────────────────── */

export interface Formatters {
  locale: Locale
  /** The BCP-47 tag handed to `Intl` — `en-GB` / `it-IT`. */
  intlLocale: string

  number: (value: number, options?: Intl.NumberFormatOptions) => string
  integer: (value: number) => string
  decimal: (value: number, digits?: number) => string
  compact: (value: number) => string
  percent: (value: number, digits?: number) => string
  signed: (value: number, digits?: number) => string
  currency: (amount: number, currency?: string, options?: Intl.NumberFormatOptions) => string
  currencyCents: (cents: number, currency?: string, options?: Intl.NumberFormatOptions) => string
  bytes: (value: number) => string
  list: (items: readonly string[], type?: Intl.ListFormatType) => string

  date: (value: DateInput, options?: ZoneOption) => string
  dateLong: (value: DateInput, options?: ZoneOption) => string
  dateShort: (value: DateInput, options?: ZoneOption) => string
  dateNumeric: (value: DateInput, options?: ZoneOption) => string
  dateTime: (value: DateInput, options?: ZoneOption) => string
  time: (value: DateInput, options?: ZoneOption) => string
  weekday: (value: DateInput, width?: NameWidth, options?: ZoneOption) => string
  monthYear: (value: DateInput, options?: ZoneOption) => string
  range: (from: DateInput, to: DateInput, options?: ZoneOption) => string

  relative: (value: DateInput, base?: DateInput) => string
  relativeCompact: (value: DateInput, base?: DateInput) => string
  duration: (minutes: number, style?: DurationStyle) => string
  durationSeconds: (seconds: number, style?: DurationStyle) => string

  weekdayNames: (width?: NameWidth) => readonly string[]
  monthNames: (width?: NameWidth) => readonly string[]
}

/** Binds every formatter to a locale. Use `useFormat()` inside components. */
export const formattersFor = (locale: Locale): Formatters => ({
  locale,
  intlLocale: intlLocaleOf(locale),

  number: (value, options) => formatNumber(locale, value, options),
  integer: (value) => formatInteger(locale, value),
  decimal: (value, digits) => formatDecimal(locale, value, digits),
  compact: (value) => formatCompactNumber(locale, value),
  percent: (value, digits) => formatPercent(locale, value, digits),
  signed: (value, digits) => formatSigned(locale, value, digits),
  currency: (amount, currency, options) => formatCurrency(locale, amount, currency, options),
  currencyCents: (cents, currency, options) => formatCurrencyCents(locale, cents, currency, options),
  bytes: (value) => formatBytes(locale, value),
  list: (items, type) => formatList(locale, items, type),

  date: (value, options) => formatDate(locale, value, options),
  dateLong: (value, options) => formatDateLong(locale, value, options),
  dateShort: (value, options) => formatDateShort(locale, value, options),
  dateNumeric: (value, options) => formatDateNumeric(locale, value, options),
  dateTime: (value, options) => formatDateTime(locale, value, options),
  time: (value, options) => formatTime(locale, value, options),
  weekday: (value, width, options) => formatWeekday(locale, value, width, options),
  monthYear: (value, options) => formatMonthYear(locale, value, options),
  range: (from, to, options) => formatDateRange(locale, from, to, options),

  relative: (value, base) => formatRelativeTime(locale, value, base),
  relativeCompact: (value, base) => formatRelativeCompact(locale, value, base),
  duration: (minutes, style) => formatDuration(locale, minutes, style),
  durationSeconds: (seconds, style) => formatDurationSeconds(locale, seconds, style),

  weekdayNames: (width) => weekdayNames(locale, width),
  monthNames: (width) => monthNames(locale, width),
})

/**
 * The component-facing entry point. Re-renders with the locale, so a chart axis and a table
 * cell change language in the same paint.
 */
export const useFormat = (): Formatters => {
  const locale = useActiveLocale()
  return useMemo(() => formattersFor(locale), [locale])
}
